import { api, queryClient } from '@/lib/api-client.ts'
import { IssuesDataT, IssueT } from '@/routes/projectStages/issues/lib/types/issuesTypes.ts'
import { AxiosResponse } from 'axios'
import { LoaderFunctionArgs } from 'react-router-dom'
import handleApiErr from '@/lib/helpers/handleApiGerError.ts'
import { stageLocationsQuery } from '@/routes/projectStages/issue/actions.ts'
import { offlineSafeEnsureQueryData } from '@/lib/helpers/offlineSafeQuery.ts'

export const stageIssuesByIssueTypeQuery = (projectStageId: string, issueTypeId: string) => ({
  queryKey: [
    'stageIssuesByIssueType',
    { projectStageId: projectStageId, issueTypeId: issueTypeId },
  ],
  queryFn: () => issuesActionsGetIssues(projectStageId, issueTypeId),
})

export type LoaderStageIssuesOkResT = {
  projectStageId: string
  issueTypeId: string
}

export const loaderStageIssuesByIssueType = async ({
  params,
}: LoaderFunctionArgs): Promise<LoaderStageIssuesOkResT | Response | null> => {
  if (!params.projectStageId || !params.issueTypeId) {
    throw new Error('No stage data provided')
  }

  const safe = <T>(q: { queryKey: unknown[]; queryFn: () => Promise<T> }) =>
    offlineSafeEnsureQueryData(queryClient, q.queryKey, q.queryFn).catch(() => undefined)

  try {
    await Promise.all([
      safe(stageIssuesByIssueTypeQuery(params.projectStageId, params.issueTypeId)),
      safe(stageLocationsQuery(params.projectStageId)),
    ])
    return {
      projectStageId: params.projectStageId,
      issueTypeId: params.issueTypeId,
    }
  } catch (err) {
    // Check if this is a network-related error
    const isNetworkError =
      !navigator.onLine ||
      (err instanceof Error &&
        (err.message.toLowerCase().includes('network') ||
          err.message.toLowerCase().includes('failed to fetch') ||
          (err as { code?: string }).code === 'ERR_NETWORK'))

    if (isNetworkError) {
      return {
        projectStageId: params.projectStageId,
        issueTypeId: params.issueTypeId,
      }
    }
    return handleApiErr(err)
  }
}
export async function issuesActionsGetIssues(projectStageId: string, issueTypeId: string) {
  const url = `/project_stages/${projectStageId}/issue_types/${issueTypeId}/issues`
  const response: AxiosResponse = await api.get(url)
  const data: IssuesDataT = response.data
  const issues: IssueT[] = data[`hydra:member`]
  return issues
}
